import { useState } from 'react';
import { Box, Stack, Typography, Checkbox, Avatar } from '@mui/joy';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';

// Красивый мультивыбор сотрудников — как список получателей в разделе сообщений:
// выдвигающийся список с галочками, аватарками и «Выбрать всех / Снять все».
//
// Пропсы:
//   employees   — массив сотрудников [{ id, full_name?, first_name?, last_name?, avatar_url?, role? }]
//   value       — массив выбранных id
//   onChange    — (ids: number[]) => void
//   label       — подпись в шапке, когда кто-то выбран (по умолч. «Сотрудники»)
//   placeholder — подпись в шапке, когда никто не выбран
//   maxHeight   — высота прокручиваемого списка (по умолч. 220)
export default function EmployeeMultiSelect({
    employees = [],
    value = [],
    onChange,
    label = 'Сотрудники',
    placeholder = 'Выберите сотрудника(ов)',
    maxHeight = 220,
}) {
    const [open, setOpen] = useState(true);
    const selected = new Set(value);

    const nameOf = (e) =>
        (e.full_name || `${e.first_name || ''} ${e.last_name || ''}`).trim();

    const roleLabel = (role) =>
        role === 'manager' ? 'Рук.'
            : (role === 'admin' || role === 'admin_photographer') ? 'Админ'
                : 'Фото';

    const toggle = (id) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id); else next.add(id);
        onChange?.([...next]);
    };
    const selectAll = () => onChange?.(employees.map((e) => e.id));
    const clearAll = () => onChange?.([]);

    return (
        <Box sx={{
            borderRadius: 'var(--joy-radius-md)',
            bgcolor: 'rgba(162,38,226,0.06)',
            border: '1px solid rgba(162,38,226,0.15)',
            overflow: 'hidden',
        }}>
            {/* Шапка */}
            <Box
                onClick={() => employees.length > 0 && setOpen((p) => !p)}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    px: 1.5, py: 1,
                    cursor: employees.length > 0 ? 'pointer' : 'default',
                    transition: 'background .15s',
                    '&:hover': employees.length > 0 ? { bgcolor: 'rgba(162,38,226,0.1)' } : {},
                }}
            >
                <PeopleRoundedIcon sx={{ fontSize: 16, color: 'var(--joy-palette-primary-400)' }} />
                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)', flex: 1 }}>
                    {value.length > 0 ? label : placeholder}
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'var(--joy-palette-primary-300)' }}>
                    {value.length}
                </Typography>
                {employees.length > 0 && value.length !== employees.length && (
                    <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                        из {employees.length}
                    </Typography>
                )}
                {employees.length > 0 && (
                    <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', ml: 0.5 }}>
                        {open ? '▲' : '▼'}
                    </Typography>
                )}
            </Box>

            {/* Список */}
            {open && employees.length > 0 && (
                <Box sx={{
                    px: 1.5, pb: 1.5, pt: 0.5,
                    borderTop: '1px solid rgba(162,38,226,0.1)',
                    maxHeight, overflowY: 'auto',
                }}>
                    <Box sx={{ display: 'flex', gap: 1, mb: 0.8 }}>
                        <Typography
                            onClick={selectAll}
                            sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-primary-300)', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        >
                            Выбрать всех
                        </Typography>
                        <Typography
                            onClick={clearAll}
                            sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        >
                            Снять все
                        </Typography>
                    </Box>

                    <Stack spacing={0.3}>
                        {employees.map((e) => (
                            <Box
                                key={e.id}
                                onClick={() => toggle(e.id)}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1,
                                    px: 1, py: 0.5, borderRadius: 'var(--joy-radius-sm)',
                                    cursor: 'pointer', transition: 'background .12s',
                                    bgcolor: selected.has(e.id) ? 'rgba(162,38,226,0.08)' : 'transparent',
                                    '&:hover': { bgcolor: 'rgba(162,38,226,0.12)' },
                                }}
                            >
                                <Checkbox
                                    size="sm"
                                    checked={selected.has(e.id)}
                                    onChange={() => toggle(e.id)}
                                    sx={{ pointerEvents: 'none' }}
                                />
                                <Avatar src={e.avatar_url} size="sm" sx={{
                                    width: 24, height: 24, fontSize: 10, fontWeight: 700,
                                    bgcolor: 'var(--joy-palette-background-level3)', color: 'var(--joy-palette-text-tertiary)',
                                    border: '1px solid var(--joy-palette-divider)', flexShrink: 0,
                                }}>
                                    {(nameOf(e) || '').charAt(0).toUpperCase()}
                                </Avatar>
                                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-primary)', flex: 1 }}>
                                    {nameOf(e)}
                                </Typography>
                                <Typography sx={{ fontSize: 9, color: 'var(--joy-palette-text-tertiary)' }}>
                                    {roleLabel(e.role)}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            )}
        </Box>
    );
}